import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { elementScrollIntoView } from "seamless-scroll-polyfill";
import { cx } from "@nju/css";
import { useGroups } from "@nju/data-access-portal-dashboard";
import { useProducts } from "@nju/data-access-portal-product";
import { useServiceStatus } from "@nju/data-access-portal-service";
import { useUser } from "@nju/data-access-portal-user";
import { portalRoutes } from "@nju/routes";
import { useIsomorphicLayoutEffect } from "@nju/utils";
import { handleDataLayerAction } from "./utils";

type Link =
  | {
      label: string | (() => string);
      path: string;
      exact: boolean;
      exclude?: () => boolean;
      testId?: string;
    }
  | {
      label: string | (() => string);
      path: string;
      match: Array<{ path: string; exact: boolean }>;
      exclude?: () => boolean;
      testId?: string;
    };

function isLinkActive(link: Link, pathname: string) {
  if ("match" in link) {
    return link.match.some((match) => {
      return match.exact
        ? pathname === match.path
        : pathname.startsWith(match.path);
    });
  }

  return link.exact ? pathname === link.path : pathname.startsWith(link.path);
}

export function Links() {
  const { pathname, query } = useRouter();
  const { ftthServiceStatus } = useServiceStatus();
  const { ftthProducts } = useProducts();
  const { availablePaymentMethods } = useUser();
  const ref = React.useRef<HTMLDivElement>(null);
  const groups = useGroups();
  const userId = query?.userId as string | undefined;

  const isAnyActiveNumber = groups?.[0]?.members.some((member) => {
    return member.primaryMsisdn;
  });

  const links: Link[] = React.useMemo(() => {
    return [
      {
        label: "start",
        path: userId
          ? portalRoutes.mojeKonto.index.get({
              mode: "mobile",
              ...(userId && { userId }),
            })
          : portalRoutes.mojeKonto.index.path(),
        match: [
          {
            path: portalRoutes.mojeKonto.index.path(),
            exact: true,
          },
          {
            path: portalRoutes.mojeKonto.wygenerujNoweHaslo.path(),
            exact: true,
          },
          {
            path: portalRoutes.mojeKonto.index.path(),
            exact: true,
          },
          {
            path: portalRoutes.mojeKonto.platnosc.index.path(),
            exact: true,
          },
          {
            path: portalRoutes.mojeKonto.zmienTerminInstalacji.path(),
            exact: true,
          },
          {
            path: portalRoutes.mojeKonto.platnosc.blik.index.path(),
            exact: true,
          },
          {
            path: portalRoutes.mojeKonto.platnosc.blik.potwierdz.path(),
            exact: true,
          },
          {
            path: portalRoutes.mojeKonto.platnosc.bladPlatnosciOnline.path(),
            exact: true,
          },
          {
            path: portalRoutes.mojeKonto.dodajKarteSim.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.niewystarczajacaKwotaNaKoncie.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.bladPlatnosci.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.anulujZamowienie.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.dodajPelnySim.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.dodajKarteSim.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.anulujPrzeniesienie.index.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.mojaKarta.index.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.mojaKarta.blokowanieKartySim.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.mojaKarta.odblokowanieKartySim.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.mojaKarta.ustawieniaNumeru.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.mojaKarta.zmienNumer.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.uslugiDodatkowe.index.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.dodajKodPoufny.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.zmienKodPoufny.path(),
            exact: false,
          },
          {
            path: portalRoutes.mojeKonto.zamowienieZlozone.path(),
            exact: false,
          },
        ],
      },
      {
        label: "mój profil",
        path: portalRoutes.mojeKonto.mojProfil.index.path(),
        exact: false,
      },
      {
        label: () => {
          if (ftthProducts.hasProducts && isAnyActiveNumber) {
            return "moje usługi";
          }

          return "moja usługa";
        },
        path: portalRoutes.mojeKonto.mojeUslugi.index.get({
          ...(userId && { userId }),
        }),
        match: [
          {
            path: portalRoutes.mojeKonto.mojeUslugi.index.path(),
            exact: true,
          },
          {
            path: portalRoutes.mojeKonto.mojeUslugi.zrezygnujZUslugi.path(),
            exact: true,
          },
          {
            path: portalRoutes.mojeKonto.mojeUslugi.reaktywacja.uslugaReaktywowana.path(),
            exact: true,
          },
        ],
        exact: false,
        exclude: () => {
          if (!ftthProducts && !isAnyActiveNumber) {
            return true;
          }

          if (
            ftthProducts &&
            ftthServiceStatus === "PendingCancellation" &&
            !isAnyActiveNumber
          ) {
            return true;
          }

          if (ftthProducts.hasNoProducts && !isAnyActiveNumber) {
            return true;
          }

          return false;
        },
      },
      {
        label: "płatności",
        path: portalRoutes.mojeKonto.platnosci.index.path(),
        match: [
          {
            path: portalRoutes.mojeKonto.platnosci.index.path(),
            exact: false,
          },
        ],
        exclude: () => {
          return availablePaymentMethods.length === 0;
        },
      },
      {
        label: "pomoc",
        path: portalRoutes.mojeKonto.pomoc.index.path(),
        exact: false,
      },
    ];
  }, [
    userId,
    ftthProducts,
    isAnyActiveNumber,
    ftthServiceStatus,
    availablePaymentMethods.length,
  ]);

  useIsomorphicLayoutEffect(() => {
    const activeIndex = links.findIndex((link) => {
      isLinkActive(link, pathname);
    });

    if (ref.current) {
      const activeElement = ref.current.querySelector(
        `a:nth-child(${activeIndex + 1})`,
      );

      if (activeElement) {
        elementScrollIntoView(activeElement, {
          inline: "start",
          behavior: "auto",
          block: "nearest",
        });
      }
    }
  }, [pathname]);

  return (
    <nav
      aria-label="Nawigacja panelu klienta"
      data-cy="navigation-panel"
      ref={ref}
    >
      {links.map((link) => {
        const { label, exclude, path } = link;

        if (typeof exclude === "function" && exclude() === true) {
          // eslint-disable-next-line unicorn/no-null
          return null;
        }

        const resolvedLabel = typeof label === "function" ? label() : label;

        return (
          <div
            className={cx(
              "inline-block relative text-size-20 h-full transition-all duration-[0.2s] ease-in focus:text-cerulean hover:text-cerulean before:content-[''] before:absolute before:bottom-0 before:h-[3px] before:w-full before:transition-colors before:duration-[0.2] [&:not(:last-child)]:mr-6 only-mobile:text-size-14",
              {
                "pb-[24px] text-cerulean font-bold only-mobile:pb-[13px] before:bg-cerulean pointer-events-none":
                  isLinkActive(link, pathname),
              },
            )}
            key={resolvedLabel}
          >
            <Link
              href={path}
              onClick={() => {
                handleDataLayerAction(resolvedLabel);
              }}
            >
              {resolvedLabel}
            </Link>
          </div>
        );
      })}
    </nav>
  );
}
